'use client';

import type { AdSlotProps } from '@/app_components/ads/Adslot';
import { InStream } from '@/app_components/ads/InStream';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { SLOTS_LIMIT } from '@/app_lib/htmlParser/inserter/rules/dotCom/embedAdSlots';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

interface InstreamAdProps extends AdSlotProps {
  className?: string;
  dataCy?: string;
  isMobileAd?: boolean;
  isDesktopAd?: boolean;
  desktopAdIndex?: number;
}

/**
 * Renders an in-stream advertisement slot within the content.
 *
 * @param {AdSlotProps} props - The properties for the ad slot.
 * @param {number} [props.index=0] - The index of the ad slot (0-indexed).
 * @param {string} props.placementName - The name of the ad placement.
 * @returns The rendered ad slot component, or null if ads should be hidden or configuration is missing.
 */
export const InStreamAd = ({
  index = 0,
  desktopAdIndex = 0,
  placementName,
  className = '',
  dataCy = ContentPlacement.InStream,
  isMobileAd = true,
  isDesktopAd = true,
}: InstreamAdProps) => {
  const isMobile = useMediaQuery(Breakpoints.Mobile);

  let adIndex = index;
  if (!isMobile) {
    adIndex = desktopAdIndex;
  }

  // We are creating the ads for both mobile and desktop on the server
  // On the client, we delete the one that isn't necessary (e.g. delete desktop ads on mobile and vice versa)
  const dontShowOnMobile = isMobile && (!isMobileAd || adIndex >= SLOTS_LIMIT);
  const dontShowOnDesktop = !isMobile && (!isDesktopAd || adIndex >= SLOTS_LIMIT);

  // This is to remove ads that shouldn't be shown on mobile or desktop
  if (dontShowOnDesktop || dontShowOnMobile) {
    // the data-empty true is being targetted in the inserter so it can hide the element and prevent flex gaps
    return <div data-empty={true} />;
  }

  return (
    <div
      className={cn(
        'w-full',
        adIndex === 0
          ? // styles applied only to the first instream ad with an active right rail and no paywall
            `group-[:has(#rightRail):not(:has(.paywallActive))]/article:flex group-[:has(#rightRail):not(:has(.paywallActive))]/article:absolute 
          group-[:has(#rightRail):not(:has(.paywallActive))]/article:left-0 group-[:has(#rightRail):not(:has(.paywallActive))]/article:right-0
          group-[:has(#rightRail):not(:has(.paywallActive))]/article:min-h-[280px] group-[:has(#rightRail):not(:has(.paywallActive))]/article:bg-secondary
          group-[:has(#rightRail):not(:has(.paywallActive))]/article:border-y-1 group-[:has(#rightRail):not(:has(.paywallActive))]/article:stroke-secondary`
          : // styles applied to target parallax ads, they are restricted to the article content's width
            'group-[:has(#rightRail):not(:has(.paywallActive))]/article:max-w-[745px] group-[:has(#rightRail):not(:has(.paywallActive))]/article:overflow-x-hidden',
      )}
    >
      <InStream
        className={className}
        dataCy={dataCy}
        index={adIndex}
        placementName={placementName}
      />
    </div>
  );
};
